import axios from 'axios';
import { SecureStorage } from './sessionManager';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 secondes
});

// Intercepteur de requête
api.interceptors.request.use(
  (config) => {
    const token = SecureStorage.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      SecureStorage.updateActivity(); // Mettre à jour l'activité à chaque requête
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponse amélioré
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Erreur réseau (pas de réponse du serveur)
    if (!error.response) {
      console.error('Erreur réseau:', error.message);
      return Promise.reject({
        ...error,
        userMessage: 'Erreur de connexion au serveur. Vérifiez votre connexion internet.',
      });
    }

    const status = error.response?.status;

    switch (status) {
      case 401:
        // Token expiré ou invalide
        console.warn('Session expirée - redirection vers login');
        SecureStorage.clearAuth();
        
        // Éviter les redirections en boucle
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?reason=session_expired';
        }
        break;

      case 403:
        // Accès interdit - utiliser le message du backend s'il existe
        const backendMessage = error.response?.data?.detail;
        if (backendMessage && typeof backendMessage === 'string') {
          error.userMessage = backendMessage;
        } else if (backendMessage && typeof backendMessage === 'object' && backendMessage.message) {
          error.userMessage = backendMessage.message;
        } else {
          error.userMessage = 'Vous n\'avez pas les permissions nécessaires pour cette action.';
        }
        break;

      case 404:
        // Ressource non trouvée
        const notFoundDetail = error.response?.data?.detail;
        if (notFoundDetail && typeof notFoundDetail === 'object' && notFoundDetail.message) {
          error.userMessage = notFoundDetail.message;
        } else if (notFoundDetail && typeof notFoundDetail === 'string') {
          error.userMessage = notFoundDetail;
        } else {
          error.userMessage = 'La ressource demandée est introuvable. Elle a peut-être été supprimée.';
        }
        break;

      case 409:
        // Conflit (ex: email déjà utilisé, doublon, etc.)
        const conflictDetail = error.response?.data?.detail;
        if (conflictDetail && typeof conflictDetail === 'object' && conflictDetail.message) {
          error.userMessage = conflictDetail.message;
        } else if (conflictDetail && typeof conflictDetail === 'string') {
          error.userMessage = conflictDetail;
        } else {
          error.userMessage = 'Cette ressource existe déjà. Veuillez vérifier vos données.';
        }
        break;
      
      case 400:
        // Erreur de validation ou données invalides
        const validationDetail = error.response?.data?.detail;
        if (validationDetail && typeof validationDetail === 'object' && validationDetail.message) {
          error.userMessage = validationDetail.message;
        } else if (validationDetail && typeof validationDetail === 'string') {
          error.userMessage = validationDetail;
        } else if (Array.isArray(validationDetail)) {
          // Erreurs de validation Pydantic
          const messages = validationDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
          error.userMessage = `Erreur de validation: ${messages}`;
        } else {
          error.userMessage = 'Données invalides. Veuillez vérifier les champs du formulaire.';
        }
        break;

      case 500:
      case 502:
      case 503:
        // Erreur serveur
        error.userMessage = 'Erreur serveur. Veuillez réessayer dans quelques instants.';
        break;

      case 429:
        // Trop de requêtes
        error.userMessage = 'Trop de requêtes. Veuillez patienter avant de réessayer.';
        break;

      default:
        error.userMessage = 'Une erreur est survenue.';
    }

    return Promise.reject(error);
  }
);

// Helper function to format error messages for display
export const formatErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  // Utiliser le message utilisateur personnalisé s'il existe
  if (error.userMessage) {
    return error.userMessage;
  }

  // Erreur réseau
  if (error.code === 'ECONNABORTED') {
    return 'La requête a pris trop de temps. Vérifiez votre connexion.';
  }

  if (error.code === 'ERR_NETWORK') {
    return 'Erreur de connexion au serveur.';
  }

  // Erreur du backend
  if (!error.response?.data?.detail) {
    return defaultMessage;
  }

  const detail = error.response.data.detail;

  // If detail is a string, return it directly
  if (typeof detail === 'string') {
    return detail;
  }

  // If detail is an array (Pydantic validation errors)
  if (Array.isArray(detail)) {
    return detail.map(err => {
      const field = err.loc ? err.loc.join(' -> ') : 'Champ';
      return `${field}: ${err.msg}`;
    }).join(', ');
  }

  // If detail is an object with structured error
  if (typeof detail === 'object' && detail.message) {
    // Format: { code: "EMAIL_EXISTS", message: "Cet email est déjà utilisé", field: "email" }
    return detail.message;
  }
  
  // If detail is an object without message
  if (typeof detail === 'object') {
    return JSON.stringify(detail);
  }

  return defaultMessage;
};

// Helper to extract error field for highlighting
export const getErrorField = (error) => {
  const detail = error.response?.data?.detail;
  
  // Structured error with field
  if (typeof detail === 'object' && detail.field) {
    return detail.field;
  }
  
  // Pydantic validation error
  if (Array.isArray(detail) && detail.length > 0 && detail[0].loc) {
    return detail[0].loc[detail[0].loc.length - 1];
  }
  
  return null;
};

// Fonction pour vérifier la santé du serveur
export const checkServerHealth = async () => {
  try {
    const response = await axios.get(`${API_URL.replace('/api', '')}/health`, {
      timeout: 5000,
    });
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

export default api;
